import React, { useState } from "react";
import { Form, Image } from "antd";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import Dragger from "antd/es/upload/Dragger.js";
import { UploadDocumentApi } from "../../../../api/request/kyc.js";
import { setUserDetails } from "../../../../store/Slices/userSlice.js";
import LoadableButton from "../../../../components/buttons/LoadableButton.jsx";
import UploadDoc from "../../../../assets/UploadDoc.jsx";
import Lock from "../../../../assets/Lock.jsx";
import Delete from "../../../../assets/Delete.jsx";

const ACCEPTED_FORMATS = ".jpg,.jpeg,.png";

const fileUploadRules = (message) => [
  {
    required: true,
    validator: (_, value) => {
      if (!value || value.length === 0) {
        return Promise.reject(new Error(message));
      }
      return Promise.resolve();
    },
  },
];

const extractOriginFiles = (fileList = []) =>
  fileList.map((file) => file.originFileObj).filter(Boolean);

const KycForm = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [fileFrontList, setFileFrontList] = useState([]);
  const [fileBackList, setFileBackList] = useState([]);
  const [fileOtherList, setFileOtherList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const syncUploadField = (fieldName, fileList, setter) => {
    setter(fileList);
    form.setFieldsValue({ [fieldName]: fileList });
    if (submitted) {
      form.validateFields([fieldName]).catch(() => {});
    }
  };

  const clearUploadField = (fieldName, setter) => {
    setter([]);
    form.setFieldsValue({ [fieldName]: [] });
    if (submitted) {
      form.validateFields([fieldName]).catch(() => {});
    }
  };

  const renderUploadContent = (
    fileList,
    emptyContent,
    altText,
    fieldName,
    setter,
    accentColor
  ) => {
    if (fileList.length < 1) {
      return emptyContent;
    }

    return (
      <div className="kyc-upload-preview">
        <img
          src={URL.createObjectURL(fileList[0].originFileObj)}
          alt={altText}
          className="kyc-preview-image"
        />
        <div className="kyc-preview-actions">
          <button
            type="button"
            className="kyc-preview-btn kyc-preview-btn--view"
            style={{ color: accentColor, borderColor: `${accentColor}40` }}
            onClick={(e) => {
              e.stopPropagation();
              handlePreview(fileList[0]);
            }}
          >
            View
          </button>
          <button
            type="button"
            className="kyc-preview-btn kyc-preview-btn--remove"
            onClick={(e) => {
              e.stopPropagation();
              clearUploadField(fieldName, setter);
            }}
          >
            <Delete />
          </button>
        </div>
      </div>
    );
  };

  const renderUploadPlaceholder = (title, accentColor) => (
    <div className="kyc-upload-placeholder">
      <div
        className="kyc-upload-icon"
        style={{ backgroundColor: `${accentColor}12`, color: accentColor }}
      >
        <UploadDoc color={accentColor} />
      </div>
      <p className="kyc-upload-title">{title}</p>
      <p className="kyc-upload-hint">
        Drag & drop or{" "}
        <span style={{ color: accentColor }}>browse files</span>
      </p>
      <span className="kyc-upload-badge">JPG · JPEG · PNG</span>
    </div>
  );

  const handleSubmit = async (values) => {
    const uploadedFront = extractOriginFiles(values.UploadDocumentFront);
    const uploadedBack = extractOriginFiles(values.UploadDocumentBack);
    const uploadedOther = extractOriginFiles(values.OtherDocument);

    const formData = new FormData();
    formData.append("NationalID", true);
    formData.append("UserId", userDetails?._id);
    formData.append("UploadDocument", uploadedFront[0]);
    formData.append("UploadDocument", uploadedBack[0]);
    formData.append("UploadSignature", uploadedBack[0]);
    uploadedOther.forEach((file) => {
      formData.append("OtherDocument", file);
    });

    try {
      setIsLoading(true);
      const apiHeaders = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      const { data } = await UploadDocumentApi(formData, apiHeaders);
      if (data?.success) {
        dispatch(setUserDetails({ ...userDetails, kycStatus: "Pending" }));
        toast.success(data?.message);
        form.resetFields();
        setFileFrontList([]);
        setFileBackList([]);
        setFileOtherList([]);
        setSubmitted(false);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message || "Failed to upload KYC documents."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFields = [
    {
      name: "UploadDocumentFront",
      label: "Front Side of ID",
      fileList: fileFrontList,
      setter: setFileFrontList,
      className: "front-upload",
      accentColor: "#7367F0",
      emptyTitle: "Upload front side",
      altText: "front",
      rules: fileUploadRules("Please upload the front side of your ID."),
    },
    {
      name: "UploadDocumentBack",
      label: "Back Side of ID",
      fileList: fileBackList,
      setter: setFileBackList,
      className: "back-upload",
      accentColor: "#28C76F",
      emptyTitle: "Upload back side",
      altText: "back",
      rules: fileUploadRules("Please upload the back side of your ID."),
    },
    {
      name: "OtherDocument",
      label: "Financial Document(Pan Card, etc.)",
      fileList: fileOtherList,
      setter: setFileOtherList,
      className: "other-upload",
      accentColor: "#FF9F43",
      emptyTitle: "Upload financial document",
      altText: "financial document",
      rules: fileUploadRules("Please upload the financial document."),
    },
  ];

  return (
    <div className="kyc-container">
      <div className="kyc-page">
        <div className="kyc-form-card">
            <div className="kyc-form-header">
              <div className="kyc-form-header__icon">
                <Lock color="#FF9F43" />
              </div>
              <div>
                <h1 className="kyc-form-header__title">Upload KYC Documents</h1>
                <p className="kyc-form-header__subtitle">
                  Upload clear images of your valid ID (front and back) along with
                  the required supporting document to complete verification.
                </p>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              onFinish={handleSubmit}
              onFinishFailed={() => {
                setSubmitted(true);
                toast.error("Please complete all required document uploads.");
              }}
            >
              <div className="kyc-upload-row">
                {uploadFields.map(
                  ({
                    name,
                    label,
                    fileList,
                    setter,
                    className,
                    accentColor,
                    emptyTitle,
                    altText,
                    rules,
                  }) => (
                    <div
                      key={name}
                      className="kyc-upload-card"
                      style={{ "--kyc-accent": accentColor }}
                    >
                      <div className="kyc-upload-card__header">
                        <span
                          className="kyc-upload-card__dot"
                          style={{ backgroundColor: accentColor }}
                        />
                        <label className="kyc-upload-card__label">
                          {label}
                          <span className="text-red-500">*</span>
                        </label>
                      </div>

                      <Form.Item
                        name={name}
                        rules={rules}
                        validateTrigger={submitted ? ["onChange", "onSubmit"] : "onSubmit"}
                        className="kyc-upload-form-item"
                      >
                        <Dragger
                          accept={ACCEPTED_FORMATS}
                          showUploadList={false}
                          beforeUpload={() => false}
                          maxCount={1}
                          fileList={fileList}
                          onPreview={handlePreview}
                          onChange={({ fileList: nextFileList }) =>
                            syncUploadField(name, nextFileList.slice(-1), setter)
                          }
                          className={`kyc-dragger ${className}`}
                          disabled={fileList.length > 0}
                        >
                          <div
                            className="kyc-dragger-inner"
                            onDragOver={(e) =>
                              fileList.length > 0 && e.preventDefault()
                            }
                            onDrop={(e) =>
                              fileList.length > 0 && e.preventDefault()
                            }
                          >
                            {renderUploadContent(
                              fileList,
                              renderUploadPlaceholder(emptyTitle, accentColor),
                              altText,
                              name,
                              setter,
                              accentColor
                            )}
                          </div>
                        </Dragger>
                      </Form.Item>
                    </div>
                  )
                )}
              </div>

              {previewImage && (
                <Image
                  wrapperStyle={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) =>
                      !visible && setPreviewImage(""),
                  }}
                  src={previewImage}
                />
              )}

              <div className="kyc-form-footer">
                <LoadableButton
                  className="kyc-submit-btn bg-primary text-white text-base font-semibold rounded-xl px-14 py-3 themeHover duration-500"
                  disabled={isLoading}
                  type="submit"
                  lable="Send Request"
                  isLoading={isLoading}
                  loadingLable="Sending Request..."
                />
              </div>
            </Form>
        </div>
      </div>
    </div>
  );
};

export default KycForm;
